import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { Lock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AdminLogin = () => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { login, user, isLoading } = useAdminAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (!isLoading && user) {
    return <Navigate to="/admin" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const result = await login(credentials.username, credentials.password);
    
    if (result.success) {
      toast({
        title: t('admin.login.loginSuccess'),
        description: t('admin.login.welcomeMessage')
      });
      navigate('/admin');
    } else {
      toast({
        title: t('admin.login.loginFailed'),
        description: result.error || t('admin.login.invalidCredentials'),
        variant: 'destructive'
      });
    }
    
    setIsSubmitting(false);
  };

  const handleInputChange = (field: string, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-8 glass border-glow">
        <div className="text-center mb-8">
          <div className="p-4 rounded-full bg-gradient-cyber w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl font-bold text-glow-cyber mb-2">{t('admin.login.title')}</h1>
          <p className="text-muted-foreground">
            {t('admin.login.subtitle')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {t('admin.login.username')}
            </Label>
            <Input
              id="username"
              type="text"
              value={credentials.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder={t('admin.login.username')}
              className="glass border-surface-variant focus:border-cyber-blue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {t('admin.login.password')}
            </Label>
            <Input
              id="password"
              type="password"
              value={credentials.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              placeholder={t('admin.login.password')}
              className="glass border-surface-variant focus:border-cyber-blue"
              required
            />
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-cyber text-primary-foreground hover-glow"
            disabled={isSubmitting}
          >
            {isSubmitting ? t('admin.login.signingIn') : t('admin.login.signIn')}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default AdminLogin;